import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  ArrowRight,
  ArrowLeft,
  Check,
  Calendar,
  Users,
  Mail,
  Phone,
  MessageSquare,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Translations for the calculator
const calculatorTranslations = {
  fi: {
    loading: 'Ladataan...',
    companyNotFound: 'Yritystä ei löytynyt',
    connectionError: 'Yhteysvirhe',
    thankYou: 'Kiitos tarjouspyynnöstä!',
    requestReceived: 'Olemme vastaanottaneet pyyntösi ja otamme sinuun yhteyttä pian.',
    estimatedPrice: 'Arvioitu hinta',
    step: 'Vaihe',
    // Steps
    eventTitle: 'Tilaisuus',
    guestsTitle: 'Vieraat',
    startersTitle: 'Alkuruoat',
    saladsTitle: 'Salaatit',
    mainCourseTitle: 'Pääruoka',
    sidesTitle: 'Lisukkeet',
    breadsTitle: 'Leivät',
    dessertsTitle: 'Jälkiruoat',
    drinksTitle: 'Juomat',
    servicesTitle: 'Lisäpalvelut',
    deliveryTitle: 'Toimitus',
    contactTitle: 'Yhteystiedot',
    // Event step
    whatEvent: 'Millainen tilaisuus?',
    otherWhat: 'Muu, mikä?',
    whatEventPlaceholder: 'Kerro millainen tilaisuus...',
    // Guests step
    howManyGuests: 'Montako vierasta?',
    minimumGuests: 'Vähintään',
    persons: 'henkilöä',
    eventDate: 'Tilaisuuden päivämäärä (valinnainen)',
    // Menu steps
    selectOptional: 'Valitse haluamasi (valinnainen)',
    canSelectMultiple: 'Voit valita useamman',
    selected: 'Valittu',
    // Main courses
    pork: 'Possu',
    beef: 'Härkä',
    chicken: 'Kana',
    fish: 'Kala',
    vegetarian: 'Kasvis',
    soups: 'Keitot',
    // Delivery step
    pickup: 'Nouto',
    delivery: 'Kuljetus',
    deliveryAddress: 'Toimitusosoite',
    deliveryAddressPlaceholder: 'Katuosoite, postinumero ja kaupunki',
    // Ready menus
    readyMenusTitle: 'Valmiit menut',
    readyMenusDesc: 'Valitse valmis menupaketti tai rakenna oma',
    buildOwn: 'Rakenna oma menu',
    buildOwnDesc: 'Valitse yksittäiset tuotteet vaihe vaiheelta',
    orSelectPackage: 'Tai valitse valmis menu:',
    perPerson: '/hlö',
    contents: 'Sisältö',
    selectedMenu: 'Valittu menu',
    // Contact step
    summary: 'Yhteenveto',
    eventLabel: 'Tilaisuus',
    guestCount: 'Henkilömäärä',
    mainCourses: 'Pääruoat',
    deliveryAddressLabel: 'Toimitusosoite',
    name: 'Nimi',
    namePlaceholder: 'Nimesi',
    email: 'Sähköposti',
    emailPlaceholder: 'sahkoposti@esimerkki.fi',
    phone: 'Puhelin',
    phonePlaceholder: '+358 40 123 4567',
    message: 'Viesti',
    messagePlaceholder: 'Lisätietoja tilaisuudesta...',
    // Navigation
    previous: 'Edellinen',
    next: 'Seuraava',
    sending: 'Lähetetään...',
    sendRequest: 'Lähetä tarjouspyyntö',
    requestSent: 'Tarjouspyyntö lähetetty!',
    sendError: 'Virhe lähetyksessä',
    other: 'Muu'
  },
  en: {
    loading: 'Loading...',
    companyNotFound: 'Company not found',
    connectionError: 'Connection error',
    thankYou: 'Thank you for your quote request!',
    requestReceived: 'We have received your request and will contact you soon.',
    estimatedPrice: 'Estimated price',
    step: 'Step',
    // Steps
    eventTitle: 'Event',
    guestsTitle: 'Guests',
    startersTitle: 'Starters',
    saladsTitle: 'Salads',
    mainCourseTitle: 'Main course',
    sidesTitle: 'Sides',
    breadsTitle: 'Breads',
    dessertsTitle: 'Desserts',
    drinksTitle: 'Drinks',
    servicesTitle: 'Services',
    deliveryTitle: 'Delivery',
    contactTitle: 'Contact',
    // Event step
    whatEvent: 'What kind of event?',
    otherWhat: 'Other, what?',
    whatEventPlaceholder: 'Tell us about your event...',
    // Guests step
    howManyGuests: 'How many guests?',
    minimumGuests: 'Minimum',
    persons: 'persons',
    eventDate: 'Event date (optional)',
    // Menu steps
    selectOptional: 'Select your options (optional)',
    canSelectMultiple: 'You can select multiple',
    selected: 'Selected',
    // Main courses
    pork: 'Pork',
    beef: 'Beef',
    chicken: 'Chicken',
    fish: 'Fish',
    vegetarian: 'Vegetarian',
    soups: 'Soups',
    // Delivery step
    pickup: 'Pickup',
    delivery: 'Delivery',
    deliveryAddress: 'Delivery address',
    deliveryAddressPlaceholder: 'Street address, postal code and city',
    // Ready menus
    readyMenusTitle: 'Ready menus',
    readyMenusDesc: 'Choose a ready-made menu package or build your own',
    buildOwn: 'Build your own menu',
    buildOwnDesc: 'Select individual items step by step',
    orSelectPackage: 'Or select a ready menu:',
    perPerson: '/person',
    contents: 'Contents',
    selectedMenu: 'Selected menu',
    // Contact step
    summary: 'Summary',
    eventLabel: 'Event',
    guestCount: 'Guest count',
    mainCourses: 'Main courses',
    deliveryAddressLabel: 'Delivery address',
    name: 'Name',
    namePlaceholder: 'Your name',
    email: 'Email',
    emailPlaceholder: 'email@example.com',
    phone: 'Phone',
    phonePlaceholder: '+1 234 567 8900',
    message: 'Message',
    messagePlaceholder: 'Additional information about your event...',
    // Navigation
    previous: 'Previous',
    next: 'Next',
    sending: 'Sending...',
    sendRequest: 'Send quote request',
    requestSent: 'Quote request sent!',
    sendError: 'Error sending request',
    other: 'Other'
  }
};

const DynamicCalculator = () => {
  const { slug } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Get translations based on company language
  const t = calculatorTranslations[company?.language || 'fi'];
  
  const [formData, setFormData] = useState({
    eventType: '',
    eventTypeOther: '', // For "Muu, mikä?" option
    guestCount: 50,
    eventDate: '',
    menuType: 'custom', // 'custom' or 'ready'
    readyMenu: null, // Selected ready menu
    selections: {
      starters: [],
      salads: [],
      mainCourses: [], // Changed to array for multiple selection
      sides: [],
      breads: [],
      desserts: [],
      drinks: [],
      services: []
    },
    delivery: 'pickup', // pickup or delivery
    deliveryAddress: '', // Address for delivery
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerMessage: ''
  });

  useEffect(() => {
    fetchCompany();
  }, [slug]);

  const fetchCompany = async () => {
    try {
      const response = await fetch(`${API_URL}/api/companies/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setCompany(data);
      } else {
        setError('notFound');
      }
    } catch (err) {
      setError('connection');
    } finally {
      setLoading(false);
    }
  };

  const getMenuByCategory = (category) => {
    return company?.menu_items?.filter(item => item.category === category) || [];
  };

  const getMenuBySubcategory = (category, subcategory) => {
    return company?.menu_items?.filter(item => item.category === category && item.subcategory === subcategory) || [];
  };

  const toggleSelection = (category, item) => {
    setFormData(prev => {
      const current = prev.selections[category] || [];
      const exists = current.find(i => i.id === item.id);
      return {
        ...prev,
        selections: {
          ...prev.selections,
          [category]: exists 
            ? current.filter(i => i.id !== item.id)
            : [...current, item]
        }
      };
    });
  };

  const toggleMainCourse = (item, subcategory) => {
    setFormData(prev => {
      const current = prev.selections.mainCourses || [];
      const exists = current.find(i => i.id === item.id);
      return {
        ...prev,
        selections: {
          ...prev.selections,
          mainCourses: exists
            ? current.filter(i => i.id !== item.id)
            : [...current, { ...item, subcategory }]
        }
      };
    });
  };

  const toggleService = (service) => {
    setFormData(prev => {
      const current = prev.selections.services || [];
      const exists = current.find(s => s.id === service.id);
      return {
        ...prev,
        selections: {
          ...prev.selections,
          services: exists
            ? current.filter(s => s.id !== service.id)
            : [...current, service]
        }
      };
    });
  };

  const calculateTotal = () => {
    let total = 0;
    const guests = formData.guestCount;
    const { selections } = formData;

    // If ready menu selected, use its price
    if (formData.menuType === 'ready' && formData.readyMenu) {
      total += (formData.readyMenu.price_per_person || 0) * guests;
    } else {
      // Per-person items
      ['starters', 'salads', 'sides', 'breads', 'desserts', 'drinks'].forEach(cat => {
        (selections[cat] || []).forEach(item => {
          total += item.price * guests;
        });
      });

      // Main courses (now multiple)
      (selections.mainCourses || []).forEach(item => {
        total += item.price * guests;
      });
    }

    // Services (always applicable)
    (selections.services || []).forEach(service => {
      if (service.price_type === 'per_person') {
        total += service.price * guests;
      } else if (service.price_type === 'fixed' || service.price_type === 'per_hour') {
        total += service.price;
      }
    });

    // Delivery
    if (formData.delivery === 'delivery' && company?.delivery_fee) {
      total += company.delivery_fee;
    }

    return total;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    
    // Combine event type with "other" text if applicable
    const isOther = formData.eventType === 'Muu, mikä?' || formData.eventType === 'Other, what?';
    const finalEventType = isOther && formData.eventTypeOther
      ? `${t?.other || 'Muu'}: ${formData.eventTypeOther}`
      : formData.eventType;
    
    try {
      const response = await fetch(`${API_URL}/api/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_slug: slug,
          event_type: finalEventType,
          guest_count: formData.guestCount,
          event_date: formData.eventDate,
          menu_type: formData.menuType,
          ready_menu: formData.menuType === 'ready' ? formData.readyMenu : null,
          selections: {
            ...formData.selections,
            delivery: formData.delivery,
            deliveryAddress: formData.delivery === 'delivery' ? formData.deliveryAddress : null
          },
          total_price: calculateTotal(),
          customer_name: formData.customerName,
          customer_email: formData.customerEmail,
          customer_phone: formData.customerPhone,
          customer_message: formData.customerMessage
        })
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success(t?.requestSent || 'Tarjouspyyntö lähetetty!');
      } else {
        toast.error(t?.sendError || 'Virhe lähetyksessä');
      }
    } catch (err) {
      toast.error(t?.connectionError || 'Yhteysvirhe');
    } finally {
      setSubmitting(false);
    }
  };

  // Steps are now dynamic based on available menu items, disabled categories, and menu type
  const getActiveSteps = () => {
    const hasReadyMenus = (company?.ready_menus || []).length > 0;
    const allowCustomMenu = company?.allow_custom_menu !== false;
    const disabledCategories = company?.disabled_categories || [];
    
    // Food category steps (only used if building custom menu)
    const foodSteps = [
      { id: 'starters', title: t?.startersTitle || 'Alkuruoat', category: 'alkuruoat' },
      { id: 'salads', title: t?.saladsTitle || 'Salaatit', category: 'salaatit' },
      { id: 'main', title: t?.mainCourseTitle || 'Pääruoka', category: 'paaruoat' },
      { id: 'sides', title: t?.sidesTitle || 'Lisukkeet', category: 'lisukkeet' },
      { id: 'breads', title: t?.breadsTitle || 'Leivät', category: 'leivat' },
      { id: 'desserts', title: t?.dessertsTitle || 'Jälkiruoat', category: 'jalkiruoat' },
      { id: 'drinks', title: t?.drinksTitle || 'Juomat', category: 'juomat' },
    ];
    
    // Filter food steps based on disabled categories and item availability
    const activeFoodSteps = foodSteps.filter(step => {
      if (disabledCategories.includes(step.category)) return false;
      const items = company?.menu_items?.filter(item => item.category === step.category) || [];
      return items.length > 0;
    });
    
    // Check if custom menu is actually available
    const hasCustomMenuOptions = allowCustomMenu && activeFoodSteps.length > 0;
    
    // Build final steps list
    const finalSteps = [
      { id: 'event', title: t?.eventTitle || 'Tilaisuus', category: null },
      { id: 'guests', title: t?.guestsTitle || 'Vieraat', category: null },
    ];
    
    // Add ready menus step if ready menus exist AND (custom is allowed OR we need to show the selection)
    // If only ready menus (no custom allowed), we still show the step but without "build own" option
    if (hasReadyMenus) {
      finalSteps.push({ id: 'readyMenus', title: t?.readyMenusTitle || 'Valmiit menut', category: null });
    }
    
    // Add food steps only if:
    // 1. Custom menu is allowed AND
    // 2. User chose to build custom menu (or there are no ready menus)
    if (hasCustomMenuOptions && (formData.menuType === 'custom' || !hasReadyMenus)) {
      finalSteps.push(...activeFoodSteps);
    }
    
    // Services (always show if available)
    if ((company?.services || []).length > 0) {
      finalSteps.push({ id: 'services', title: t?.servicesTitle || 'Lisäpalvelut', category: 'services' });
    }
    
    // Delivery and contact always at the end
    finalSteps.push({ id: 'delivery', title: t?.deliveryTitle || 'Toimitus', category: null });
    finalSteps.push({ id: 'contact', title: t?.contactTitle || 'Yhteystiedot', category: null });
    
    return finalSteps;
  };

  const steps = company ? getActiveSteps() : [];
  
  // Map step index to step id for canProceed
  const currentStepId = steps[step]?.id;

  const canProceed = () => {
    switch (currentStepId) {
      case 'event': 
        // If "Muu, mikä?" or "Other, what?" selected, require the text field too
        if (formData.eventType === 'Muu, mikä?' || formData.eventType === 'Other, what?') {
          return formData.eventTypeOther.trim() !== '';
        }
        return formData.eventType !== '';
      case 'guests': return formData.guestCount >= (company?.min_guests || 10);
      case 'readyMenus': 
        // If custom menu not allowed, must select a ready menu
        if (company?.allow_custom_menu === false) {
          return formData.readyMenu !== null;
        }
        // Otherwise can choose custom or ready menu
        return formData.menuType === 'custom' || formData.readyMenu !== null;
      case 'contact': return formData.customerName && formData.customerEmail;
      default: return true;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  // Get translations now that company might be loaded
  const lang = company?.language || 'fi';
  const txt = calculatorTranslations[lang];

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-stone-900">
            {error === 'notFound' ? txt.companyNotFound : txt.connectionError}
          </h1>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: company.primary_color + '10' }}>
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: company.primary_color }}>
            <Check className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-stone-900 mb-2">{txt.thankYou}</h1>
          <p className="text-stone-600 mb-4">
            {txt.requestReceived}
          </p>
          <p className="text-lg font-semibold" style={{ color: company.primary_color }}>
            {txt.estimatedPrice}: {calculateTotal().toFixed(2)}€
          </p>
        </div>
      </div>
    );
  }

  const primaryColor = company?.primary_color || '#f59e0b';
  
  // Build logo URL - handle both absolute URLs and relative paths
  const logoUrl = company?.logo_url 
    ? (company.logo_url.startsWith('http') 
        ? company.logo_url 
        : `${API_URL}${company.logo_url}`)
    : null;

  return (
    <div className="min-h-screen bg-stone-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          {logoUrl && (
            <img 
              src={logoUrl} 
              alt={company.name} 
              className="h-16 mx-auto mb-4"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <h1 className="text-2xl font-bold text-stone-900">{company?.name}</h1>
        </div>

        {/* Progress */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-medium text-stone-700">{steps[step].title}</span>
            <span className="text-stone-500">{txt.step} {step + 1}/{steps.length}</span>
          </div>
          <div className="h-2 bg-stone-100 rounded-full">
            <div 
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${((step + 1) / steps.length) * 100}%`, backgroundColor: primaryColor }}
            />
          </div>
        </div>

        {/* Calculator Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="p-6">
            {/* Event Type */}
            {currentStepId === 'event' && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-stone-900">{txt.whatEvent}</h2>
                <div className="grid grid-cols-2 gap-3">
                  {(company?.event_types || []).map(type => {
                    // Translate "Muu, mikä?" / "Other, what?" based on calculator language
                    const isOtherType = type === 'Muu, mikä?' || type === 'Other, what?';
                    const displayType = isOtherType ? txt.otherWhat : type;
                    
                    return (
                      <button
                        key={type}
                        onClick={() => setFormData({ ...formData, eventType: type, eventTypeOther: isOtherType ? formData.eventTypeOther : '' })}
                        className={`p-4 rounded-xl border-2 text-center transition-all ${
                          formData.eventType === type
                            ? 'border-amber-500 bg-amber-50'
                            : 'border-stone-200 hover:border-amber-300'
                        }`}
                        style={formData.eventType === type ? { borderColor: primaryColor, backgroundColor: primaryColor + '10' } : {}}
                      >
                        {displayType}
                      </button>
                    );
                  })}
                </div>
                
                {/* Show text field when "Muu, mikä?" or "Other, what?" is selected */}
                {(formData.eventType === 'Muu, mikä?' || formData.eventType === 'Other, what?') && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-stone-700 mb-2">{txt.otherWhat}</label>
                    <Input
                      value={formData.eventTypeOther}
                      onChange={(e) => setFormData({ ...formData, eventTypeOther: e.target.value })}
                      placeholder={txt.whatEventPlaceholder}
                      className="border-stone-200"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Guests */}
            {currentStepId === 'guests' && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-stone-900">{txt.howManyGuests}</h2>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min={company?.min_guests || 10}
                    max={company?.max_guests || 500}
                    value={formData.guestCount}
                    onChange={(e) => setFormData({ ...formData, guestCount: parseInt(e.target.value) })}
                    className="flex-1"
                    style={{ accentColor: primaryColor }}
                  />
                  <span className="text-2xl font-bold w-20 text-center" style={{ color: primaryColor }}>
                    {formData.guestCount}
                  </span>
                </div>
                <p className="text-sm text-stone-500">{txt.minimumGuests} {company?.min_guests || 10} {txt.persons}</p>
                
                <div className="mt-4">
                  <label className="block text-sm font-medium text-stone-700 mb-2">{txt.eventDate}</label>
                  <Input
                    type="date"
                    value={formData.eventDate}
                    onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                    className="border-stone-200"
                  />
                </div>
              </div>
            )}

            {/* Ready Menus */}
            {currentStepId === 'readyMenus' && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-stone-900">{txt.readyMenusTitle}</h2>
                <p className="text-sm text-stone-500">{company?.allow_custom_menu !== false ? txt.readyMenusDesc : txt.selectOptional}</p>
                
                {/* Option to build own menu - only show if allowed */}
                {company?.allow_custom_menu !== false && (
                  <button
                    onClick={() => setFormData({ ...formData, menuType: 'custom', readyMenu: null })}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      formData.menuType === 'custom'
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-stone-200 hover:border-amber-300'
                    }`}
                    style={formData.menuType === 'custom' ? { borderColor: primaryColor, backgroundColor: primaryColor + '10' } : {}}
                  >
                    <div className="font-medium">{txt.buildOwn}</div>
                    <div className="text-sm text-stone-500">{txt.buildOwnDesc}</div>
                  </button>
                )}
                
                {/* Ready menu packages */}
                {(company?.ready_menus || []).length > 0 && (
                  <div className="space-y-3">
                    {company?.allow_custom_menu !== false && (
                      <p className="text-sm font-medium text-stone-700 mt-4">{txt.orSelectPackage}</p>
                    )}
                    {(company?.ready_menus || []).map(menu => (
                      <button
                        key={menu.id || menu.name}
                        onClick={() => setFormData({ ...formData, menuType: 'ready', readyMenu: menu })}
                        className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                          formData.readyMenu?.name === menu.name
                            ? 'border-amber-500 bg-amber-50'
                            : 'border-stone-200 hover:border-amber-300'
                        }`}
                        style={formData.readyMenu?.name === menu.name ? { borderColor: primaryColor, backgroundColor: primaryColor + '10' } : {}}
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-medium">{menu.name}</div>
                          <div className="font-semibold" style={{ color: primaryColor }}>
                            {menu.price_per_person}€ {txt.perPerson}
                          </div>
                        </div>
                        {menu.description && (
                          <div className="text-sm text-stone-500 mt-1">{menu.description}</div>
                        )}
                        {menu.items && menu.items.length > 0 && (
                          <div className="text-xs text-stone-400 mt-2">
                            {txt.contents}: {menu.items.join(', ')}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Show selected ready menu summary */}
                {formData.menuType === 'ready' && formData.readyMenu && (
                  <div className="mt-4 p-3 rounded-xl" style={{ backgroundColor: primaryColor + '10' }}>
                    <p className="text-sm font-medium" style={{ color: primaryColor }}>
                      {txt.selectedMenu}: {formData.readyMenu.name} ({formData.readyMenu.price_per_person}€ {txt.perPerson})
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Starters */}
            {currentStepId === 'starters' && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-stone-900">{txt.startersTitle}</h2>
                <p className="text-sm text-stone-500">{txt.selectOptional}</p>
                <div className="grid grid-cols-2 gap-3">
                  {getMenuByCategory('alkuruoat').map(item => (
                    <button
                      key={item.id}
                      onClick={() => toggleSelection('starters', item)}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${
                        formData.selections.starters.find(i => i.id === item.id)
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-stone-200 hover:border-amber-300'
                      }`}
                      style={formData.selections.starters.find(i => i.id === item.id) ? { borderColor: primaryColor, backgroundColor: primaryColor + '10' } : {}}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Salads */}
            {currentStepId === 'salads' && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-stone-900">{txt.saladsTitle}</h2>
                <div className="grid grid-cols-2 gap-3">
                  {getMenuByCategory('salaatit').map(item => (
                    <button
                      key={item.id}
                      onClick={() => toggleSelection('salads', item)}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${
                        formData.selections.salads.find(i => i.id === item.id)
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-stone-200 hover:border-amber-300'
                      }`}
                      style={formData.selections.salads.find(i => i.id === item.id) ? { borderColor: primaryColor, backgroundColor: primaryColor + '10' } : {}}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Main Course */}
            {currentStepId === 'main' && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-stone-900">{txt.mainCourseTitle}</h2>
                <p className="text-sm text-stone-500">{txt.canSelectMultiple}</p>
                {['possu', 'harka', 'kana', 'kala', 'kasvis', 'keitto'].map(subcategory => {
                  const items = getMenuBySubcategory('paaruoat', subcategory);
                  if (items.length === 0) return null;
                  
                  const labels = { 
                    possu: txt.pork, 
                    harka: txt.beef, 
                    kana: txt.chicken, 
                    kala: txt.fish, 
                    kasvis: txt.vegetarian, 
                    keitto: txt.soups 
                  };
                  
                  return (
                    <div key={subcategory} className="space-y-2">
                      <h3 className="text-sm font-medium text-stone-700">{labels[subcategory]}</h3>
                      <div className="grid grid-cols-2 gap-2">
                        {items.map(item => (
                          <button
                            key={item.id}
                            onClick={() => toggleMainCourse(item, subcategory)}
                            className={`p-3 rounded-xl border-2 text-center text-sm transition-all ${
                              formData.selections.mainCourses?.find(i => i.id === item.id)
                                ? 'border-amber-500 bg-amber-50'
                                : 'border-stone-200 hover:border-amber-300'
                            }`}
                            style={formData.selections.mainCourses?.find(i => i.id === item.id) ? { borderColor: primaryColor, backgroundColor: primaryColor + '10' } : {}}
                          >
                            {item.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {formData.selections.mainCourses?.length > 0 && (
                  <div className="mt-4 p-3 bg-stone-50 rounded-xl">
                    <p className="text-sm text-stone-600">
                      {txt.selected}: {formData.selections.mainCourses.map(m => m.name).join(', ')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Sides */}
            {currentStepId === 'sides' && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-stone-900">{txt.sidesTitle}</h2>
                <div className="grid grid-cols-2 gap-3">
                  {getMenuByCategory('lisukkeet').map(item => (
                    <button
                      key={item.id}
                      onClick={() => toggleSelection('sides', item)}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${
                        formData.selections.sides.find(i => i.id === item.id)
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-stone-200 hover:border-amber-300'
                      }`}
                      style={formData.selections.sides.find(i => i.id === item.id) ? { borderColor: primaryColor, backgroundColor: primaryColor + '10' } : {}}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Breads */}
            {currentStepId === 'breads' && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-stone-900">{txt.breadsTitle}</h2>
                <div className="grid grid-cols-2 gap-3">
                  {getMenuByCategory('leivat').map(item => (
                    <button
                      key={item.id}
                      onClick={() => toggleSelection('breads', item)}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${
                        formData.selections.breads.find(i => i.id === item.id)
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-stone-200 hover:border-amber-300'
                      }`}
                      style={formData.selections.breads.find(i => i.id === item.id) ? { borderColor: primaryColor, backgroundColor: primaryColor + '10' } : {}}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Desserts */}
            {currentStepId === 'desserts' && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-stone-900">{txt.dessertsTitle}</h2>
                <div className="grid grid-cols-2 gap-3">
                  {getMenuByCategory('jalkiruoat').map(item => (
                    <button
                      key={item.id}
                      onClick={() => toggleSelection('desserts', item)}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${
                        formData.selections.desserts.find(i => i.id === item.id)
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-stone-200 hover:border-amber-300'
                      }`}
                      style={formData.selections.desserts.find(i => i.id === item.id) ? { borderColor: primaryColor, backgroundColor: primaryColor + '10' } : {}}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Drinks */}
            {currentStepId === 'drinks' && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-stone-900">{txt.drinksTitle}</h2>
                <div className="grid grid-cols-2 gap-3">
                  {getMenuByCategory('juomat').map(item => (
                    <button
                      key={item.id}
                      onClick={() => toggleSelection('drinks', item)}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${
                        formData.selections.drinks.find(i => i.id === item.id)
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-stone-200 hover:border-amber-300'
                      }`}
                      style={formData.selections.drinks.find(i => i.id === item.id) ? { borderColor: primaryColor, backgroundColor: primaryColor + '10' } : {}}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Services */}
            {currentStepId === 'services' && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-stone-900">{txt.servicesTitle}</h2>
                <div className="space-y-3">
                  {(company?.services || []).map(service => (
                    <button
                      key={service.id}
                      onClick={() => toggleService(service)}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                        formData.selections.services.find(s => s.id === service.id)
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-stone-200 hover:border-amber-300'
                      }`}
                      style={formData.selections.services.find(s => s.id === service.id) ? { borderColor: primaryColor, backgroundColor: primaryColor + '10' } : {}}
                    >
                      {service.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Delivery */}
            {currentStepId === 'delivery' && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-stone-900">{txt.deliveryTitle}</h2>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setFormData({ ...formData, delivery: 'pickup' })}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      formData.delivery === 'pickup'
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-stone-200 hover:border-amber-300'
                    }`}
                    style={formData.delivery === 'pickup' ? { borderColor: primaryColor, backgroundColor: primaryColor + '10' } : {}}
                  >
                    <div className="font-medium">{txt.pickup}</div>
                    <div className="text-sm text-stone-500">0€</div>
                  </button>
                  <button
                    onClick={() => setFormData({ ...formData, delivery: 'delivery' })}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      formData.delivery === 'delivery'
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-stone-200 hover:border-amber-300'
                    }`}
                    style={formData.delivery === 'delivery' ? { borderColor: primaryColor, backgroundColor: primaryColor + '10' } : {}}
                  >
                    <div className="font-medium">{txt.delivery}</div>
                    <div className="text-sm text-stone-500">+{company?.delivery_fee || 50}€</div>
                  </button>
                </div>
                
                {/* Show address field when delivery is selected */}
                {formData.delivery === 'delivery' && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-stone-700 mb-2">{txt.deliveryAddress}</label>
                    <Input
                      value={formData.deliveryAddress}
                      onChange={(e) => setFormData({ ...formData, deliveryAddress: e.target.value })}
                      placeholder={txt.deliveryAddressPlaceholder}
                      className="border-stone-200"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Contact */}
            {currentStepId === 'contact' && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-stone-900">{txt.contactTitle}</h2>
                
                {/* Summary */}
                <div className="bg-stone-50 rounded-xl p-4 mb-4">
                  <h3 className="font-medium text-stone-700 mb-2">{txt.summary}</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-stone-500">{txt.eventLabel}</span>
                      <span>{(formData.eventType === 'Muu, mikä?' || formData.eventType === 'Other, what?') ? `${txt.other}: ${formData.eventTypeOther}` : formData.eventType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-500">{txt.guestCount}</span>
                      <span>{formData.guestCount} {lang === 'en' ? 'ppl' : 'hlö'}</span>
                    </div>
                    {/* Show ready menu if selected */}
                    {formData.menuType === 'ready' && formData.readyMenu && (
                      <div className="flex justify-between">
                        <span className="text-stone-500">{txt.selectedMenu}</span>
                        <span className="text-right">{formData.readyMenu.name}</span>
                      </div>
                    )}
                    {/* Show custom selections if building own menu */}
                    {formData.menuType === 'custom' && formData.selections.mainCourses?.length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-stone-500">{txt.mainCourses}</span>
                        <span className="text-right">{formData.selections.mainCourses.map(m => m.name).join(', ')}</span>
                      </div>
                    )}
                    {formData.delivery === 'delivery' && formData.deliveryAddress && (
                      <div className="flex justify-between">
                        <span className="text-stone-500">{txt.deliveryAddressLabel}</span>
                        <span className="text-right">{formData.deliveryAddress}</span>
                      </div>
                    )}
                  </div>
                  <div className="border-t border-stone-200 mt-3 pt-3">
                    <div className="flex justify-between font-semibold">
                      <span>{txt.estimatedPrice}</span>
                      <span style={{ color: primaryColor }}>{calculateTotal().toFixed(2)}€</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">{txt.name} *</label>
                  <Input
                    value={formData.customerName}
                    onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                    required
                    placeholder={txt.namePlaceholder}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">{txt.email} *</label>
                  <Input
                    type="email"
                    value={formData.customerEmail}
                    onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                    required
                    placeholder={txt.emailPlaceholder}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">{txt.phone}</label>
                  <Input
                    value={formData.customerPhone}
                    onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                    placeholder={txt.phonePlaceholder}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">{txt.message}</label>
                  <Textarea
                    value={formData.customerMessage}
                    onChange={(e) => setFormData({ ...formData, customerMessage: e.target.value })}
                    placeholder={txt.messagePlaceholder}
                    rows={3}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Price Bar */}
          <div className="border-t border-stone-200 px-6 py-4 bg-stone-50 flex items-center justify-between">
            <div>
              <span className="text-sm text-stone-500">{txt.estimatedPrice}</span>
              <div className="text-2xl font-bold" style={{ color: primaryColor }}>
                {calculateTotal().toFixed(2)}€
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="border-t border-stone-200 px-6 py-4 flex justify-between">
            <button
              onClick={() => setStep(Math.max(0, step - 1))}
              disabled={step === 0}
              className="flex items-center gap-2 px-4 py-2 text-stone-500 hover:text-stone-800 disabled:opacity-50"
            >
              <ArrowLeft className="w-4 h-4" />
              {txt.previous}
            </button>
            {currentStepId !== 'contact' ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="flex items-center gap-2 px-6 py-2 rounded-xl text-white disabled:opacity-50"
                style={{ backgroundColor: primaryColor }}
              >
                {txt.next}
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!canProceed() || submitting}
                className="flex items-center gap-2 px-6 py-2 rounded-xl text-white disabled:opacity-50"
                style={{ backgroundColor: primaryColor }}
              >
                {submitting ? txt.sending : txt.sendRequest}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicCalculator;
